import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Avatar } from '@heroui/react';
import cn from 'classnames';
import {
  getMyProfile,
  isProfileStaff,
  isProfileStudent,
  type ITeacherProfileResponse,
} from '@/entities/Profile';
import { Section } from '@/shared/ui/Section';
import { UserAvatar } from '@/shared/ui/UserAvatar';
import FileIcon from '@/shared/assets/icons/file-document.svg';
import UserIcon from '@/shared/assets/icons/user-voice.svg';
import { routerPath } from '@/shared/config/router';
import { Svg } from '@/shared/ui/Svg';
import { InvoiceBalanceTiles } from './components/InvoiceBalanceTiles';
import { SupportModal } from './components/SupportModal';

interface UserInfoProps {
  className?: string;
  /** Сводка по счетам. Нужна только в профиле — в настройках финансы не к месту. */
  withInvoiceBalance?: boolean;
}

export const UserInfo = ({ className, withInvoiceBalance = false }: UserInfoProps) => {
  const user = useSelector(getMyProfile);
  const [isSupportOpen, setIsSupportOpen] = useState(false);
  const isStaff = Boolean(user && isProfileStaff(user));
  const isStudent = Boolean(user && isProfileStudent(user));
  const lineManager =
    user && isProfileStaff(user)
      ? ((user as ITeacherProfileResponse).profile?.lineManager ?? null)
      : null;

  const fullName = user
    ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username
    : 'User';

  return (
    <div className={cn(className, 'min-w-[200px] w-full max-w-[346px] h-fit flex flex-col gap-3')}>
      <Section
        className={cn('w-full flex flex-col px-6 py-6 items-center gap-y-4 rounded-[16px] bg-white shadow-sm'
      )}
    >
        <Avatar
          src={user?.avatar || undefined}
          name={fullName}
          className="w-[164px] h-[164px] text-6xl"
          classNames={{
          base: 'bg-neutral-200',
          img: 'object-cover',
        }}
      />
        {user && (
        <>
          <div className="flex flex-col gap-y-2 w-full">
            <p className="text-[32px] font-medium">{fullName}</p>
            <span className="bg-red w-fit px-2 py-[6px] rounded-[6px] text-white text-sm font-medium">
              ID: IHCL{user.id.toString().padStart(6, '0')}
            </span>
          </div>

          {isStaff && (
            <div className="flex gap-2 w-full">
              <p className="text-sm font-medium text-black-secondary">Line Manager:</p>
              {lineManager ? (
                <UserAvatar
                  avatar={lineManager.avatar}
                  userName={
                    `${lineManager.firstName} ${lineManager.lastName}`.trim() || lineManager.username
                  }
                  href={`/staff/${lineManager.id}`}
                  size={8}
                  className="justify-start w-full"
                />
              ) : (
                <span className="text-sm text-grey-secondary">—</span>
              )}
            </div>
          )}
          {/* Social Links */}
          {/* <div className="flex flex-col gap-2 w-full">
            <a
              href={instagramLink}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between p-2 rounded-lg hover:bg-neutral-50 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Svg Icon={InstagramIcon} className="w-5 h-5" />
                <span className="text-sm font-medium">Instagram</span>
              </div>
              <Svg Icon={ArrowUpRightIcon} className="w-4 h-4 text-grey-secondary" />
            </a>
            <a
              href={linkedinLink}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between p-2 rounded-lg hover:bg-neutral-50 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Svg Icon={LinkedInIcon} className="w-5 h-5" />
                <span className="text-sm font-medium">LinkedIn</span>
              </div>
              <Svg Icon={ArrowUpRightIcon} className="w-4 h-4 text-grey-secondary" />
            </a>
          </div> */}

          <div className="flex flex-col gap-3 w-full mt-2">
            <Link
              to={isStaff ? `/profile/${routerPath.teacherProfileRequestCreate}` : '/profile/request/create'}
              className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-red text-base font-medium text-white"
            >
              Create request
              <Svg Icon={FileIcon} className="h-6 w-6" />
            </Link>
            <button
              type="button"
              className="flex h-12 w-full cursor-pointer items-center justify-center gap-2 rounded-xl border border-red text-base font-medium text-red"
              onClick={() => setIsSupportOpen(true)}
            >
              Support
              <Svg Icon={UserIcon} className="h-6 w-6" />
            </button>
          </div>
        </>
      )}
      </Section>

      {user && withInvoiceBalance && isStudent && <InvoiceBalanceTiles />}

      <SupportModal isOpen={isSupportOpen} onClose={() => setIsSupportOpen(false)} />
    </div>
  );
};
